import { Box, CircularProgress, Stack } from "@mui/joy";
import React, { useEffect, useRef, useState } from "react";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import Link from "../../components/common/Link";
import useGetDeviceInfo from "../../hooks/useGetDeviceInfo";
import useGetUrls from "../../hooks/useGetUrls";
import GoogleLoginButtonPromotion from "../../components/user/GoogleLoginButtonPromotion";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { userSimpleAtom } from "../../recoil/userSimpleAtom";
import AppleLoginButtonPromotion from "../../components/user/AppleLoginButtonPromotion";
import useAutoLoginPromotion from "../../hooks/useAutoLoginPromotion";
import useMixpanelMKT from "../../hooks/useMixpanelMKT";
import { motion, PanInfo } from "framer-motion";

import promotion1 from "../../static/image/promotion/login/1.png";
import promotion2 from "../../static/image/promotion/login/2.png";
import promotion3 from "../../static/image/promotion/login/3.png";
import promotion4 from "../../static/image/promotion/login/4.png";
import promotion5 from "../../static/image/promotion/login/5.png";
import promotion6 from "../../static/image/promotion/login/6.png";
import promotion7 from "../../static/image/promotion/login/7.png";
import promotion8 from "../../static/image/promotion/login/8.png";
import promotion9 from "../../static/image/promotion/login/9.png";
import promotion10 from "../../static/image/promotion/login/10.png";
import distancingLogo from "../../static/image/common/distancing_logo.png";
import { webTestTypeAtom } from "../../recoil/webTestTypeAtom";
import dividerImg from "../../static/image/login/login_divider.svg";
import { extractHexColor } from "../../styles/hexOpacity";
import KakaoLoginButtonPromotion from "../../components/user/KakaoLoginButtonPromotion";

const carouselItems = [
  { title: "매일 주어지는 활동지", image: promotion1 },
  { title: "이론 배우기", image: promotion2 },
  { title: "전담 코치의 리딩", image: promotion3 },
  { title: "패턴 발견하기", image: promotion4 },
  { title: "명상 훈련", image: promotion5 },
  { title: "생각 다루기", image: promotion6 },
  { title: "추적하며 관리하기", image: promotion7 },
  { title: "100일간의 여정", image: promotion8 },
  { title: "나 이해하기", image: promotion9 },
  { title: "가치 발견하기", image: promotion10 },
];

const ITEM_WIDTH = 226;
const ITEM_GAP = 12.5;
const MAX_VIEWPORT_WIDTH = ITEM_WIDTH * 1.8 + ITEM_GAP * 2;

export default function LoginPromotion() {
  const { homeIndicatorHeight } = useGetDeviceInfo();
  const { companyInfoUrl, privacyPolicyUrl, servicePolicyUrl } = useGetUrls();
  const navigate = useNavigate();
  const { isChecking } = useAutoLoginPromotion();
  const user = useRecoilValue(userSimpleAtom);
  const [currentIndex, setCurrentIndex] = useState(0);
  const webTestType = useRecoilValue(webTestTypeAtom);

  // 터치 방향 감지를 위한 state
  const [isDraggingCarousel, setIsDraggingCarousel] = useState(false);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const directionLockedRef = useRef<boolean>(false);

  const backgroundColor = theme.vars.palette.Background.Level2;

  useEffect(() => {
    const fixedSafeAreaHex = extractHexColor(backgroundColor);
    let metaThemeColor = document.querySelector("meta[name='theme-color']");

    if (!metaThemeColor) {
      metaThemeColor = document.createElement("meta");
      metaThemeColor.setAttribute("name", "theme-color");
      document.head.appendChild(metaThemeColor);
    }

    metaThemeColor.setAttribute("content", fixedSafeAreaHex);
    document.documentElement.style.backgroundColor = fixedSafeAreaHex;
    document.body.style.backgroundColor = fixedSafeAreaHex;
  }, []);

  const trackPromotion = useMixpanelMKT({
    eventName: "로그인 페이지 진입",
    version: "v4",
  });

  function useEffectOnce(callback: () => void) {
    const ref = useRef(false);
    useEffect(() => {
      if (ref.current) return;
      ref.current = true;
      if (typeof callback === "function") {
        callback();
      }
    }, []);
  }

  useEffectOnce(() => {
    trackPromotion();
  });

  const handleDragEnd = (_: any, info: PanInfo) => {
    const threshold = 50;
    const velocityThreshold = 500;

    if (
      (info.offset.x < -threshold || info.velocity.x < -velocityThreshold) &&
      currentIndex < carouselItems.length - 1
    ) {
      setCurrentIndex((prev) => prev + 1);
    } else if (
      (info.offset.x > threshold || info.velocity.x > velocityThreshold) &&
      currentIndex > 0
    ) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  // 불필요한 수동 터치 이벤트 리스너(useEffect)와 ref 제거함

  const childrenType = (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="flex-start"
      spacing={4}
      sx={{
        width: "100%",
        boxSizing: "border-box",
        pt: 4,
      }}
    >
      <img
        src={distancingLogo}
        alt="distancing logo"
        draggable={false}
        style={{ width: "138px", height: "auto", flexShrink: 0 }}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "320px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 2, sm: 4 },
          flexShrink: 0,
        }}
      >
        <Stack spacing={"12px"} sx={{ textAlign: "center", width: "100%" }}>
          <CustomTypography
            variant="H1"
            sx={{
              color: theme.vars.palette.Content.Strong,
              fontWeight: 800,
              whiteSpace: "pre-wrap",
            }}
          >
            {"맞춤 프로그램 결과를\n" + "지금 만나보세요"}
          </CustomTypography>
          <CustomTypography
            variant="Subtitle"
            sx={{
              color: theme.vars.palette.Content.Medium,
              fontWeight: 500,
              whiteSpace: "pre-wrap",
            }}
          >
            {"효과가 입증된 지속가능한 멘탈케어"}
          </CustomTypography>
        </Stack>

        <Stack
          spacing={"6px"}
          sx={{
            width: "100%",
          }}
          alignSelf="center"
        >
          <KakaoLoginButtonPromotion />
          <AppleLoginButtonPromotion />
          <GoogleLoginButtonPromotion />
        </Stack>
      </Box>

      <img
        src={dividerImg}
        alt={"empty"}
        style={{
          width: "100%",
          maxWidth: "320px",
        }}
      />

      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          // 초기 터치 방향 판단 후 세로 스크롤 허용
          touchAction: isDraggingCarousel ? "pan-x" : "auto",
          overflow: "hidden",
        }}
        onPointerDown={(e) => {
          touchStartRef.current = { x: e.clientX, y: e.clientY };
          directionLockedRef.current = false;
          setIsDraggingCarousel(false); // 초기에는 방향 미정
        }}
        onPointerMove={(e) => {
          if (!touchStartRef.current) return;
          if (directionLockedRef.current) return; // 방향이 이미 결정되면 변경 안 함

          // 터치 시작점부터의 누적 이동량 계산
          const dx = e.clientX - touchStartRef.current.x;
          const dy = e.clientY - touchStartRef.current.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // 임계값 이상 움직였을 때 방향 결정
          if (distance > 5) {
            const angle = Math.abs(Math.atan2(dy, dx) * (180 / Math.PI));
            // 방향 결정 및 잠금
            directionLockedRef.current = true;

            // 세로 방향 (60도 ~ 120도)이면 세로 스크롤 허용 (정확히 세로일 때만)
            // 가로 방향이면 캐러셀 드래그 활성화 (더 관대하게)
            if (angle >= 60 && angle <= 120) {
              setIsDraggingCarousel(false);
            } else {
              setIsDraggingCarousel(true);
            }
          }
        }}
        onPointerUp={() => {
          touchStartRef.current = null;
          directionLockedRef.current = false;
        }}
        onPointerCancel={() => {
          touchStartRef.current = null;
          directionLockedRef.current = false;
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: `${MAX_VIEWPORT_WIDTH}px`,
            display: "flex",
            alignItems: "center",
          }}
        >
          <motion.div
            drag={isDraggingCarousel ? "x" : false}
            dragDirectionLock
            onDragEnd={handleDragEnd}
            dragMomentum={false}
            dragElastic={0.1}
            dragConstraints={{
              left: -(carouselItems.length - 1) * (ITEM_WIDTH + ITEM_GAP),
              right: 0,
            }}
            animate={{ x: -currentIndex * (ITEM_WIDTH + ITEM_GAP) }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            style={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              cursor: "grab",
              userSelect: "none",
              WebkitUserSelect: "none",
              touchAction: isDraggingCarousel ? "pan-x" : "auto",
            }}
            whileTap={{ cursor: "grabbing" }}
          >
            <Box
              sx={{
                flex: `0 0 calc(50% - ${ITEM_WIDTH / 2}px)`,
                flexShrink: 0,
              }}
            />
            {carouselItems.map((item, index) => (
              <Box
                key={index}
                sx={{
                  flex: `0 0 ${ITEM_WIDTH}px`,
                  marginRight: `${ITEM_GAP}px`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                  boxSizing: "border-box",
                }}
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: index === currentIndex ? 1 : 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <CustomTypography
                    variant="Body"
                    sx={{
                      py: 0,
                      color: theme.vars.palette.Content.Medium,
                      fontWeight: 500,
                      textAlign: "center",
                    }}
                  >
                    {item.title}
                  </CustomTypography>
                </motion.div>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    draggable={false}
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Box>
            ))}
            <Box
              sx={{
                flex: `0 0 calc(50% - ${ITEM_WIDTH / 2}px)`,
                flexShrink: 0,
              }}
            />
          </motion.div>
        </Box>
      </Box>
    </Stack>
  );

  if (isChecking) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: backgroundColor,
        }}
      >
        <CircularProgress color="neutral" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        zIndex: 1000,
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "calc(var(--vh,1vh) * 100)",
        backgroundColor: backgroundColor,
        overflowY: "auto",
        display: "flex",
        justifyContent: "center",
        WebkitOverflowScrolling: "touch",
        overscrollBehavior: "none",
      }}
    >
      <Stack
        direction={"column"}
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: "660px",
          minHeight: "100%",
          pb: `${homeIndicatorHeight}px`,
          position: "relative",
        }}
      >
        {childrenType}

        <Stack
          direction={"row"}
          spacing={"20px"}
          justifyContent="center"
          sx={{
            mt: "auto",
            mb: "40px",
            pb: "10px",
            pt: 4,
            wordBreak: "keep-all",
            flexShrink: 0,
          }}
        >
          <Link
            opacity={100}
            bold={false}
            text={"사업자 정보"}
            url={companyInfoUrl}
          />
          <Link
            opacity={100}
            bold={false}
            text={"개인정보 처리방침"}
            url={privacyPolicyUrl}
          />
          <Link
            opacity={100}
            bold={false}
            text={"서비스 이용약관"}
            url={servicePolicyUrl}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
